import { ReactNode, useEffect } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';

interface Props {
  title: string;
  /** One line under the title explaining what the page is for. */
  subtitle?: ReactNode;
  /** Page-level actions (primary action last), shown on the right. */
  actions?: ReactNode;
  /** Breadcrumb trail before the current page. Defaults to Dashboard. */
  trail?: { label: string; to: string }[];
  /** Mono kicker line above the title; when set it replaces the breadcrumb. May hold links. */
  eyebrow?: ReactNode;
  /** Extra classes on the header row (e.g. `!mb-[22px]` for the Alerts artboard). */
  className?: string;
}

export const APP_NAME = 'YADA School';

/**
 * The one page header every panel page uses: title + optional subtitle on the
 * left, actions (or the breadcrumb when there are none) on the right. Also
 * names the browser tab.
 */
export default function PageHeader({ title, subtitle, actions, eyebrow, className = '', trail = [{ label: 'Dashboard', to: '/dashboard' }] }: Props) {
  const inRouter = useInRouterContext();
  useEffect(() => {
    document.title = `${title} · ${APP_NAME}`;
  }, [title]);

  return (
    <div className={`mb-[26px] flex flex-wrap items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0 flex-[1_1_320px]">
        {eyebrow ? (
          <p className="mb-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">{eyebrow}</p>
        ) : trail.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-2.5">
            <ol className="flex flex-wrap items-center gap-1 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
              {trail.map((t) => (
                <li key={t.to} className="flex items-center gap-1">
                  {inRouter ? <Link className="hover:text-ink" to={t.to}>{t.label}</Link> : <a className="hover:text-ink" href={t.to}>{t.label}</a>}
                  <span aria-hidden>/</span>
                </li>
              ))}
            </ol>
          </nav>
        )}
        <h1 className="break-words font-display text-[30px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink sm:text-[40px]">{title}</h1>
        {subtitle && <p className="mt-2.5 text-[15px] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}
